(() => {
  const formatMoney = (cents, format) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') return window.Shopify.formatMoney(cents, format);
    const template = format || '£{{amount}}';
    const token = template.match(/\{\{\s*(amount(?:_no_decimals)?(?:_with_comma_separator|_with_space_separator|_with_apostrophe_separator)?)\s*\}\}/);
    if (!token) return (Number(cents || 0) / 100).toFixed(2);
    const noDecimals = token[1].includes('no_decimals');
    const comma = token[1].includes('comma_separator');
    const space = token[1].includes('space_separator');
    const apostrophe = token[1].includes('apostrophe_separator');
    const decimal = comma ? ',' : '.';
    const thousands = comma ? '.' : space ? ' ' : apostrophe ? "'" : ',';
    const raw = (Number(cents || 0) / 100).toFixed(noDecimals ? 0 : 2).split('.');
    raw[0] = raw[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const value = raw.length > 1 ? `${raw[0]}${decimal}${raw[1]}` : raw[0];
    return template.replace(token[0], value);
  };

  const displayVariantTitle = title => title === 'Default Title' ? '' : title;

  class WholesaleProduct extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-wholesale-variants]').textContent);
      this.config = JSON.parse(this.querySelector('[data-wholesale-config]').textContent);
      this.current = this.variants.find(v => String(v.id) === String(this.querySelector('[data-variant-id]').value)) || this.variants[0];
      this.nativeDetails = document.querySelector('[data-wholesale-native-details]');
      const nativeVariants = this.nativeDetails?.querySelector('[data-wholesale-native-variants]');
      this.nativeVariantDetails = nativeVariants ? JSON.parse(nativeVariants.textContent) : [];
      this.galleryMedia = [...this.querySelectorAll('[data-media-id]')];
      this.galleryStage = this.querySelector('[data-gallery-stage]');
      this.zoomDialog = this.querySelector('[data-zoom-dialog]');
      this.zoomImage = this.querySelector('[data-zoom-image]');
      this.zoomableMedia = this.galleryMedia.filter(media => media.querySelector('[data-zoom-src]'));
      this.currentMediaIndex = Math.max(0, this.galleryMedia.findIndex(media => media.classList.contains('is-active')));
      this.gestureCleanups = [];
      this.addEventListener('change', event => {
        if (event.target.matches('[data-variant-input]')) this.selectVariant(event.target.value, true);
      });
      this.addEventListener('click', event => this.handleClick(event));
      if (this.galleryStage && this.galleryMedia.length > 1) this.bindGalleryGesture(this.galleryStage, false);
      const zoomStage = this.querySelector('[data-zoom-stage]');
      if (zoomStage && this.zoomableMedia.length > 1) this.bindGalleryGesture(zoomStage, true);
      this.zoomKeyHandler = event => this.handleZoomKey(event);
      this.zoomDialog?.addEventListener('keydown', this.zoomKeyHandler);
      this.syncGalleryControls();
      this.updateNativeDetails(this.current);
      this.dispatchVariant();
    }

    handleClick(event) {
      const thumb = event.target.closest('[data-media-target]');
      if (thumb) {
        event.preventDefault();
        this.showMedia(thumb.dataset.mediaTarget);
      }
      if (event.target.closest('[data-gallery-previous]')) {
        event.preventDefault();
        this.navigateGallery(-1);
      }
      if (event.target.closest('[data-gallery-next]')) {
        event.preventDefault();
        this.navigateGallery(1);
      }
      if (event.target.closest('[data-zoom-previous]')) {
        event.preventDefault();
        this.navigateGallery(-1, true);
      }
      if (event.target.closest('[data-zoom-next]')) {
        event.preventDefault();
        this.navigateGallery(1, true);
      }
      if (event.target.closest('[data-quantity-minus]')) this.changeQuantity(-1);
      if (event.target.closest('[data-quantity-plus]')) this.changeQuantity(1);
      const zoom = event.target.closest('[data-zoom-src]');
      if (zoom) {
        event.preventDefault();
        if (!this.suppressGalleryClick) this.openZoom(zoom.closest('[data-media-id]'));
      }
      if (event.target.closest('[data-zoom-close]')) this.closeZoom();
    }

    disconnectedCallback() {
      this.zoomDialog?.removeEventListener('keydown', this.zoomKeyHandler);
      this.gestureCleanups?.forEach(cleanup => cleanup());
    }

    changeQuantity(delta) {
      const input = this.querySelector('[data-quantity]');
      input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + delta);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    showMedia(id) {
      const nextIndex = this.galleryMedia.findIndex(media => String(media.dataset.mediaId) === String(id));
      if (nextIndex < 0) return;
      this.currentMediaIndex = nextIndex;
      this.galleryMedia.forEach(media => {
        const active = String(media.dataset.mediaId) === String(id);
        media.hidden = !active;
        media.classList.toggle('is-active', active);
      });
      this.querySelectorAll('[data-media-target]').forEach(thumb => {
        const active = String(thumb.dataset.mediaTarget) === String(id);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (active) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      });
      this.syncGalleryControls();
      if (this.zoomDialog?.open) this.setZoomMedia(this.galleryMedia[nextIndex]);
    }

    navigateGallery(direction, inZoom = false) {
      const media = inZoom ? this.zoomableMedia : this.galleryMedia;
      if (media.length < 2) return;
      const currentId = this.galleryMedia[this.currentMediaIndex]?.dataset.mediaId;
      const index = media.findIndex(item => String(item.dataset.mediaId) === String(currentId));
      const nextIndex = Math.min(media.length - 1, Math.max(0, (index < 0 ? 0 : index) + direction));
      if (nextIndex === index) return;
      this.showMedia(media[nextIndex].dataset.mediaId);
    }

    syncGalleryControls() {
      const total = this.galleryMedia.length;
      const previous = this.querySelector('[data-gallery-previous]');
      const next = this.querySelector('[data-gallery-next]');
      if (previous) previous.disabled = total < 2 || this.currentMediaIndex <= 0;
      if (next) next.disabled = total < 2 || this.currentMediaIndex >= total - 1;
      const current = this.querySelector('[data-gallery-current]');
      const totalNode = this.querySelector('[data-gallery-total]');
      if (current) current.textContent = String(this.currentMediaIndex + 1);
      if (totalNode) totalNode.textContent = String(total);
    }

    openZoom(media) {
      if (!media || !this.zoomDialog?.showModal) return;
      this.showMedia(media.dataset.mediaId);
      this.setZoomMedia(media);
      if (!this.zoomDialog.open) this.zoomDialog.showModal();
    }

    closeZoom() {
      if (this.zoomDialog?.open) this.zoomDialog.close();
    }

    setZoomMedia(media) {
      const zoom = media?.querySelector('[data-zoom-src]');
      if (!zoom || !this.zoomImage) return;
      this.zoomImage.src = zoom.dataset.zoomSrc;
      this.zoomImage.alt = zoom.dataset.zoomAlt || '';
      const index = this.zoomableMedia.indexOf(media);
      const total = this.zoomableMedia.length;
      const previous = this.querySelector('[data-zoom-previous]');
      const next = this.querySelector('[data-zoom-next]');
      const counter = this.querySelector('[data-zoom-counter]');
      if (previous) {
        previous.hidden = total < 2;
        previous.disabled = index <= 0;
      }
      if (next) {
        next.hidden = total < 2;
        next.disabled = index < 0 || index >= total - 1;
      }
      if (counter) counter.hidden = total < 2;
      const current = this.querySelector('[data-zoom-current]');
      const totalNode = this.querySelector('[data-zoom-total]');
      if (current) current.textContent = String(index + 1);
      if (totalNode) totalNode.textContent = String(total);
    }

    handleZoomKey(event) {
      if (!this.zoomDialog?.open) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.navigateGallery(-1, true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.navigateGallery(1, true);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.closeZoom();
      }
    }

    bindGalleryGesture(surface, inZoom) {
      const state = { active: false, dragging: false, pointerId: null, startX: 0, startY: 0, deltaX: 0 };
      const visual = () => inZoom ? this.zoomImage : this.galleryMedia[this.currentMediaIndex];
      const reset = () => {
        const item = visual();
        if (item) {
          item.style.transform = '';
          item.style.transition = '';
        }
        surface.classList.remove('is-dragging');
        state.active = false;
        state.dragging = false;
        state.pointerId = null;
        state.deltaX = 0;
      };
      const pointerDown = event => {
        if (event.button !== undefined && event.button !== 0) return;
        if (event.target.closest('[data-gallery-previous], [data-gallery-next], [data-zoom-previous], [data-zoom-next], [data-zoom-close], video, iframe, model-viewer')) return;
        state.active = true;
        state.pointerId = event.pointerId;
        state.startX = event.clientX;
        state.startY = event.clientY;
        state.deltaX = 0;
      };
      const pointerMove = event => {
        if (!state.active || event.pointerId !== state.pointerId) return;
        const deltaX = event.clientX - state.startX;
        const deltaY = event.clientY - state.startY;
        if (!state.dragging) {
          if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
          if (Math.abs(deltaY) >= Math.abs(deltaX)) {
            reset();
            return;
          }
          state.dragging = true;
          surface.classList.add('is-dragging');
          surface.setPointerCapture?.(event.pointerId);
        }
        event.preventDefault();
        state.deltaX = deltaX;
        const item = visual();
        if (item) {
          item.style.transition = 'none';
          item.style.transform = `translate3d(${Math.max(-90, Math.min(90, deltaX * 0.28))}px, 0, 0)`;
        }
      };
      const pointerEnd = event => {
        if (!state.active || event.pointerId !== state.pointerId) return;
        const dragged = state.dragging;
        const deltaX = state.deltaX;
        reset();
        if (!dragged) return;
        this.suppressGalleryClick = true;
        window.setTimeout(() => { this.suppressGalleryClick = false; }, 120);
        if (Math.abs(deltaX) >= 48) this.navigateGallery(deltaX < 0 ? 1 : -1, inZoom);
      };
      surface.addEventListener('pointerdown', pointerDown);
      surface.addEventListener('pointermove', pointerMove);
      surface.addEventListener('pointerup', pointerEnd);
      surface.addEventListener('pointercancel', reset);
      this.gestureCleanups.push(() => {
        surface.removeEventListener('pointerdown', pointerDown);
        surface.removeEventListener('pointermove', pointerMove);
        surface.removeEventListener('pointerup', pointerEnd);
        surface.removeEventListener('pointercancel', reset);
      });
    }

    selectVariant(id, updateUrl = false) {
      const variant = this.variants.find(item => String(item.id) === String(id));
      if (!variant) return;
      this.current = variant;
      this.querySelectorAll('input[name="id"]').forEach(input => { input.value = variant.id; input.disabled = false; });
      this.querySelectorAll('[data-variant-card]').forEach(card => card.classList.toggle('is-selected', String(card.querySelector('input').value) === String(variant.id)));
      const radio = this.querySelector(`[data-variant-input][value="${variant.id}"]`);
      if (radio) radio.checked = true;
      this.querySelector('[data-price]').textContent = formatMoney(variant.price, this.config.moneyFormat);
      this.querySelector('[data-vat-label]').textContent = variant.taxable ? 'INC VAT' : 'Zero VAT';
      const compare = this.querySelector('[data-compare-price]');
      compare.hidden = !(variant.compare_at_price > variant.price);
      compare.textContent = variant.compare_at_price ? formatMoney(variant.compare_at_price, this.config.moneyFormat) : '';
      const saleBadge = this.querySelector('[data-sale-badge]');
      if (saleBadge) saleBadge.hidden = !(variant.compare_at_price > variant.price);
      const unit = this.querySelector('[data-unit-price]');
      if (variant.unit_price_measurement) {
        const m = variant.unit_price_measurement;
        unit.hidden = false;
        unit.querySelector('[data-unit-price-value]').textContent = formatMoney(variant.unit_price, this.config.moneyFormat);
        unit.querySelector('[data-unit-price-label]').textContent = m.reference_unit === 'item' ? this.config.unitPriceSuffix : `/ ${m.reference_value !== 1 ? m.reference_value : ''}${m.reference_unit}`;
      } else unit.hidden = true;
      this.querySelector('[data-sku]').textContent = variant.sku || '—';
      const stock = this.querySelector('[data-stock-card]');
      stock.classList.toggle('is-out', !variant.available);
      this.querySelector('[data-stock-text]').textContent = variant.available ? this.config.inStockText : this.config.outOfStockText;
      const submit = this.querySelector('.wholesale-cart-form [type="submit"]');
      submit.disabled = !variant.available;
      submit.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
      this.querySelector('[data-add-label]').textContent = variant.available ? this.config.addText : this.config.soldOutText;
      if (variant.featured_media) this.showMedia(variant.featured_media.id);
      if (updateUrl && history.replaceState) history.replaceState({}, '', `${this.dataset.productUrl || location.pathname}?variant=${variant.id}`);
      this.updateNativeDetails(variant);
      this.dispatchVariant();
    }

    updateNativeDetails(variant) {
      if (!this.nativeDetails) return;
      const details = this.nativeVariantDetails.find(item => String(item.id) === String(variant.id));
      if (!details) return;
      const updateRow = (rowSelector, valueSelector, value) => {
        const row = this.nativeDetails.querySelector(rowSelector);
        if (!row) return;
        row.hidden = !value;
        const valueNode = row.querySelector(valueSelector);
        if (valueNode) valueNode.textContent = value || '';
      };
      updateRow('[data-wholesale-barcode-row]', '[data-wholesale-barcode]', details.barcode);
      updateRow('[data-wholesale-weight-row]', '[data-wholesale-weight]', details.weight);
    }

    dispatchVariant() {
      this.dispatchEvent(new CustomEvent('wholesale:variant-change', { bubbles: true, detail: { variant: this.current, moneyFormat: this.config.moneyFormat } }));
    }
  }

  class WholesaleDescription extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-wholesale-description-content]');
      this.toggle = this.querySelector('[data-wholesale-description-toggle]');
      if (!this.content || !this.toggle) return;
      this.mobileQuery = window.matchMedia('(max-width: 560px)');
      this.expanded = false;
      this.animating = false;
      this.toggleHandler = () => this.animateTo(!this.expanded);
      this.viewportHandler = () => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.sync(false));
      };
      this.transitionHandler = event => {
        if (event.propertyName === 'height') this.finishAnimation();
      };
      this.toggle.addEventListener('click', this.toggleHandler);
      this.content.addEventListener('transitionend', this.transitionHandler);
      if (this.mobileQuery.addEventListener) this.mobileQuery.addEventListener('change', this.viewportHandler);
      else this.mobileQuery.addListener(this.viewportHandler);
      window.addEventListener('resize', this.viewportHandler, { passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (!this.animating) this.viewportHandler();
        });
        this.resizeObserver.observe(this.content);
      }
      this.content.querySelectorAll('img').forEach(image => {
        if (!image.complete) image.addEventListener('load', this.viewportHandler, { once: true });
      });
      requestAnimationFrame(() => this.sync(true));
    }

    disconnectedCallback() {
      this.toggle?.removeEventListener('click', this.toggleHandler);
      this.content?.removeEventListener('transitionend', this.transitionHandler);
      window.removeEventListener('resize', this.viewportHandler);
      cancelAnimationFrame(this.resizeFrame);
      clearTimeout(this.animationTimer);
      this.resizeObserver?.disconnect();
      if (!this.mobileQuery) return;
      if (this.mobileQuery.removeEventListener) this.mobileQuery.removeEventListener('change', this.viewportHandler);
      else this.mobileQuery.removeListener(this.viewportHandler);
    }

    collapsedHeight() {
      if (this.hasAttribute('data-wholesale-collapse-fully')) return 0;
      const styles = getComputedStyle(this.content);
      const lineHeight = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.8;
      return Math.round(lineHeight * 5);
    }

    sync(reset) {
      if (!this.mobileQuery.matches) {
        this.classList.remove('is-collapsible', 'is-expanded');
        this.content.style.height = '';
        this.animating = false;
        this.toggle.setAttribute('aria-expanded', 'true');
        return;
      }

      const collapsedHeight = this.collapsedHeight();
      const fullHeight = this.content.scrollHeight;
      if (fullHeight <= collapsedHeight + 8) {
        this.classList.remove('is-collapsible', 'is-expanded');
        this.content.style.height = '';
        this.expanded = true;
        this.toggle.setAttribute('aria-expanded', 'true');
        return;
      }

      this.classList.add('is-collapsible');
      if (reset) this.expanded = false;
      this.content.style.height = this.expanded ? 'auto' : `${collapsedHeight}px`;
      this.renderState();
    }

    animateTo(expanded) {
      if (!this.mobileQuery.matches || !this.classList.contains('is-collapsible')) return;
      clearTimeout(this.animationTimer);
      const startHeight = this.content.getBoundingClientRect().height;
      const targetHeight = expanded ? this.content.scrollHeight : this.collapsedHeight();
      this.content.style.height = `${startHeight}px`;
      this.content.getBoundingClientRect();
      this.expanded = expanded;
      this.animating = true;
      this.renderState();
      requestAnimationFrame(() => {
        this.content.style.height = `${targetHeight}px`;
      });
      this.animationTimer = setTimeout(() => this.finishAnimation(), 450);
    }

    finishAnimation() {
      if (!this.animating) return;
      clearTimeout(this.animationTimer);
      this.animating = false;
      this.content.style.height = this.expanded ? 'auto' : `${this.collapsedHeight()}px`;
    }

    renderState() {
      this.classList.toggle('is-expanded', this.expanded);
      this.toggle.setAttribute('aria-expanded', String(this.expanded));
      const readMore = this.querySelector('[data-wholesale-read-more]');
      const readLess = this.querySelector('[data-wholesale-read-less]');
      if (readMore) readMore.hidden = this.expanded;
      if (readLess) readLess.hidden = !this.expanded;
    }
  }

  class WholesaleStickyCart extends HTMLElement {
    connectedCallback() {
      this.hero = document.querySelector('wholesale-product');
      if (!this.hero) return;
      this.hidden = false;
      this.stickyContent = this.querySelector('.wholesale-sticky__inner');
      this.floatingControlRoot = document.documentElement;
      this.floatingControlRoot.classList.add('has-wholesale-sticky-cart');
      this.syncFloatingControlOffset = () => {
        const isVisible = this.classList.contains('is-visible') && !this.hidden;
        const stickyHeight = isVisible ? Math.ceil(this.getBoundingClientRect().height) : 0;
        const contentRect = this.stickyContent?.getBoundingClientRect();
        const rightInset = contentRect ? Math.max(0, Math.ceil(window.innerWidth - contentRect.right)) : 0;
        this.floatingControlRoot.style.setProperty('--wholesale-sticky-cart-height', `${stickyHeight}px`);
        this.floatingControlRoot.style.setProperty('--wholesale-sticky-cart-right-inset', `${rightInset}px`);
      };
      this.scheduleFloatingControlOffset = () => {
        cancelAnimationFrame(this.floatingControlFrame);
        this.floatingControlFrame = requestAnimationFrame(this.syncFloatingControlOffset);
      };
      this.observer = new IntersectionObserver(entries => {
        this.classList.toggle('is-visible', !entries[0].isIntersecting);
        this.scheduleFloatingControlOffset();
      }, { threshold: 0 });
      if ('ResizeObserver' in window) {
        this.sizeObserver = new ResizeObserver(this.scheduleFloatingControlOffset);
        this.sizeObserver.observe(this);
        if (this.stickyContent) this.sizeObserver.observe(this.stickyContent);
      }
      this.windowResizeHandler = this.scheduleFloatingControlOffset;
      window.addEventListener('resize', this.windowResizeHandler, { passive: true });
      const purchase = this.hero.querySelector('[data-main-purchase]');
      if (purchase) this.observer.observe(purchase);
      this.variantHandler = event => this.update(event.detail);
      document.addEventListener('wholesale:variant-change', this.variantHandler);
      this.select = this.querySelector('[data-sticky-select]');
      this.dropdown = this.querySelector('[data-sticky-dropdown]');
      this.trigger = this.querySelector('[data-sticky-select-trigger]');
      this.menu = this.querySelector('[data-sticky-select-menu]');
      this.options = [...this.querySelectorAll('[data-sticky-option]')];
      this.selectHandler = event => this.hero.selectVariant(event.target.value, true);
      this.select?.addEventListener('change', this.selectHandler);
      this.triggerHandler = () => this.toggleDropdown();
      this.optionHandler = event => {
        const option = event.target.closest('[data-sticky-option]');
        if (!option || option.disabled) return;
        this.select.value = option.dataset.value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
        this.closeDropdown(true);
      };
      this.keyHandler = event => this.handleDropdownKey(event);
      this.outsideHandler = event => {
        if (this.dropdown && !this.dropdown.contains(event.target)) this.closeDropdown();
      };
      this.trigger?.addEventListener('click', this.triggerHandler);
      this.menu?.addEventListener('click', this.optionHandler);
      this.dropdown?.addEventListener('keydown', this.keyHandler);
      document.addEventListener('click', this.outsideHandler);
      this.addButton = this.querySelector('[data-sticky-add]');
      this.mobileToggle = this.querySelector('[data-sticky-mobile-toggle]');
      this.mobileToggleHandler = () => {
        const collapsed = this.classList.toggle('is-mobile-collapsed');
        this.mobileToggle.setAttribute('aria-expanded', String(!collapsed));
        this.mobileToggle.setAttribute('aria-label', collapsed ? 'Expand sticky cart' : 'Collapse sticky cart');
        if (collapsed) this.closeDropdown();
        this.scheduleFloatingControlOffset();
      };
      this.mobileToggle?.addEventListener('click', this.mobileToggleHandler);
      this.addHandler = () => {
        if (this.addButton.disabled || this.addButton.getAttribute('aria-disabled') === 'true') return;
        this.hero.querySelector('.wholesale-cart-form [type="submit"]').click();
      };
      this.addButton.addEventListener('click', this.addHandler);
      this.scheduleFloatingControlOffset();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.sizeObserver?.disconnect();
      cancelAnimationFrame(this.floatingControlFrame);
      if (this.windowResizeHandler) window.removeEventListener('resize', this.windowResizeHandler);
      if (this.variantHandler) document.removeEventListener('wholesale:variant-change', this.variantHandler);
      this.select?.removeEventListener('change', this.selectHandler);
      this.trigger?.removeEventListener('click', this.triggerHandler);
      this.menu?.removeEventListener('click', this.optionHandler);
      this.dropdown?.removeEventListener('keydown', this.keyHandler);
      if (this.outsideHandler) document.removeEventListener('click', this.outsideHandler);
      this.mobileToggle?.removeEventListener('click', this.mobileToggleHandler);
      this.addButton?.removeEventListener('click', this.addHandler);
      if (!document.querySelector('wholesale-sticky-cart')) {
        document.documentElement.classList.remove('has-wholesale-sticky-cart');
        document.documentElement.style.removeProperty('--wholesale-sticky-cart-height');
        document.documentElement.style.removeProperty('--wholesale-sticky-cart-right-inset');
      }
    }

    toggleDropdown(forceOpen) {
      if (!this.dropdown || !this.trigger) return;
      const open = typeof forceOpen === 'boolean' ? forceOpen : !this.dropdown.classList.contains('is-open');
      this.dropdown.classList.toggle('is-open', open);
      this.trigger.setAttribute('aria-expanded', String(open));
      if (open) {
        const selected = this.options.find(option => option.getAttribute('aria-selected') === 'true' && !option.disabled);
        requestAnimationFrame(() => (selected || this.options.find(option => !option.disabled))?.focus());
      }
    }

    closeDropdown(returnFocus = false) {
      if (!this.dropdown || !this.trigger) return;
      this.dropdown.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      if (returnFocus) this.trigger.focus();
    }

    handleDropdownKey(event) {
      const enabled = this.options.filter(option => !option.disabled);
      if (!enabled.length) return;
      const activeIndex = enabled.indexOf(document.activeElement);
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDropdown(true);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!this.dropdown.classList.contains('is-open')) {
          this.toggleDropdown(true);
          return;
        }
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        const nextIndex = activeIndex < 0 ? 0 : (activeIndex + direction + enabled.length) % enabled.length;
        enabled[nextIndex].focus();
      } else if (event.key === 'Home' && this.dropdown.classList.contains('is-open')) {
        event.preventDefault();
        enabled[0].focus();
      } else if (event.key === 'End' && this.dropdown.classList.contains('is-open')) {
        event.preventDefault();
        enabled[enabled.length - 1].focus();
      } else if ((event.key === 'Enter' || event.key === ' ') && document.activeElement?.matches('[data-sticky-option]')) {
        event.preventDefault();
        document.activeElement.click();
      }
    }

    syncDropdown(variant) {
      const title = displayVariantTitle(variant.title);
      const selectedLabel = this.querySelector('[data-sticky-selected-label]');
      if (selectedLabel) selectedLabel.textContent = title;
      this.options.forEach(option => {
        const selected = String(option.dataset.value) === String(variant.id);
        option.classList.toggle('is-selected', selected);
        option.setAttribute('aria-selected', String(selected));
      });
    }

    update({ variant, moneyFormat }) {
      const select = this.select || this.querySelector('[data-sticky-select]');
      if (select) select.value = variant.id;
      this.syncDropdown(variant);
      this.closeDropdown();
      const variantTitle = this.querySelector('[data-sticky-variant]');
      const displayedTitle = displayVariantTitle(variant.title);
      variantTitle.textContent = displayedTitle;
      variantTitle.hidden = this.dataset.showVariant !== 'true' || !displayedTitle;
      this.querySelector('[data-sticky-price]').textContent = formatMoney(variant.price, moneyFormat);
      const compare = this.querySelector('[data-sticky-compare]');
      if (compare) {
        compare.hidden = this.dataset.showCompare !== 'true' || !(variant.compare_at_price > variant.price);
        compare.textContent = variant.compare_at_price ? formatMoney(variant.compare_at_price, moneyFormat) : '';
      }
      const vat = this.querySelector('[data-sticky-vat]');
      vat.hidden = this.dataset.showVat !== 'true';
      vat.textContent = variant.taxable ? this.dataset.taxableText : this.dataset.nontaxableText;
      const unit = this.querySelector('[data-sticky-unit]');
      if (this.dataset.showUnit === 'true' && variant.unit_price_measurement) {
        const measurement = variant.unit_price_measurement;
        unit.hidden = false;
        unit.querySelector('[data-sticky-unit-value]').textContent = formatMoney(variant.unit_price, moneyFormat);
        unit.querySelector('[data-sticky-unit-label]').textContent = measurement.reference_unit === 'item' ? this.dataset.unitSuffix : `/ ${measurement.reference_value !== 1 ? measurement.reference_value : ''}${measurement.reference_unit}`;
      } else unit.hidden = true;
      const button = this.querySelector('[data-sticky-add]');
      button.disabled = !variant.available;
      button.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
      button.textContent = variant.available ? this.dataset.addText : this.dataset.soldOutText;
      const image = this.querySelector('.wholesale-sticky__image');
      if (image) {
        const source = variant.featured_image?.src || this.dataset.defaultImage;
        if (source) image.src = source.replace(/(\.[a-z]+)(\?.*)?$/i, '_120x$1$2');
      }
      this.scheduleFloatingControlOffset?.();
    }
  }

  if (!customElements.get('wholesale-product')) customElements.define('wholesale-product', WholesaleProduct);
  if (!customElements.get('wholesale-description')) customElements.define('wholesale-description', WholesaleDescription);
  if (!customElements.get('wholesale-sticky-cart')) customElements.define('wholesale-sticky-cart', WholesaleStickyCart);
})();
